import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { R, btnAccent, mono } from '../lib/ui';
import { Modal } from './Modal';
import { useToast } from './Toast';

const iconButton: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  background: 'transparent',
  color: R.textSecondary,
  border: 'none',
  cursor: 'pointer',
};

export function SharePanel({ docId, onClose }: { docId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();

  const links = useQuery({ queryKey: ['share', docId], queryFn: () => api.shareList(docId) });

  const create = useMutation({
    mutationFn: () => api.shareCreate(docId, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share', docId] }),
  });

  const revoke = useMutation({
    mutationFn: (token: string) => api.shareDelete(token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['share', docId] }),
  });

  /** Буфер обмена может быть недоступен без https — отсюда и ветка с ошибкой. */
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast('Ссылка скопирована', 'ok');
    } catch {
      toast('Не удалось скопировать ссылку', 'err');
    }
  };

  const list = links.data ?? [];

  return (
    <Modal title="Поделиться документом" onClose={onClose} width={520}>
      <div style={{ fontSize: 13, color: R.textSecondary, marginBottom: 12 }}>
        Публичная ссылка даёт доступ только на чтение — без входа через Discord.
        Отозвать можно в любой момент.
      </div>

      <button
        onClick={() => create.mutate()}
        disabled={create.isPending}
        style={{ ...btnAccent, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}
      >
        <Plus size={14} /> {create.isPending ? 'Создаём…' : 'Создать ссылку'}
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((link) => {
          const url = `${window.location.origin}${link.url}`;

          return (
            <div
              key={link.token}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                background: R.bgInput,
                border: `1px solid ${R.border}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: mono,
                    fontSize: 12,
                    color: R.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {url}
                </div>
                <div style={{ fontSize: 11, color: R.textMuted, marginTop: 3 }}>
                  Создана {new Date(link.createdAt).toLocaleDateString('ru')}
                </div>
              </div>

              <button onClick={() => copy(url)} title="Скопировать" style={iconButton}>
                <Copy size={14} />
              </button>

              <button
                onClick={() => revoke.mutate(link.token)}
                title="Отозвать"
                style={{ ...iconButton, color: R.danger }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}

        {list.length === 0 && !links.isLoading && (
          <div
            style={{
              padding: 16,
              textAlign: 'center',
              color: R.textMuted,
              fontSize: 13,
              border: `1px dashed ${R.border}`,
            }}
          >
            Пока ссылок нет
          </div>
        )}
      </div>
    </Modal>
  );
}
